import { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { ChevronLeft, ChevronRight, RefreshCw, Loader2 } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useDealsDnd } from '@/hooks/useDealsDnd'
import { activeStages, closedStages, futureStages } from '@/components/Deals/deals-constants'
import { useDealsByStage } from '@/components/Deals/useDealsByStage'
import { KanbanColumnV2 } from '@/components/DealsV2/KanbanColumnV2'
import type { Deal, DealStage, MainTab } from '@/types'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/authStore'
import './deals-v2-kanban.css'

interface KanbanBoardV2Props {
  visibleDeals: Deal[]
  mainTab: MainTab
  onSelectDeal: (deal: Deal) => void
  onMoveStage?: (dealId: string, newStage: DealStage) => void
  areValuesVisible?: boolean
  loading?: boolean
  error?: string | null
  onRetry?: () => void
}

export function KanbanBoardV2({
  visibleDeals,
  mainTab,
  onSelectDeal,
  onMoveStage,
  areValuesVisible = false,
  loading = false,
  error = null,
  onRetry,
}: KanbanBoardV2Props) {
  const { user } = useAuthStore()
  const containerRef = useRef<HTMLDivElement>(null)
  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(false)

  const stages = useMemo(
    () =>
      mainTab === 'closed'
        ? closedStages
        : mainTab === 'future'
          ? futureStages
          : activeStages,
    [mainTab],
  )

  const dealsByStage = useDealsByStage(visibleDeals, stages)

  const checkScroll = useCallback(() => {
    if (!containerRef.current) return
    const { scrollLeft, scrollWidth, clientWidth } = containerRef.current
    setShowLeft(scrollLeft > 20)
    setShowRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 5)
  }, [])

  useEffect(() => {
    checkScroll()
    const timer = setTimeout(checkScroll, 150)
    window.addEventListener('resize', checkScroll)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', checkScroll)
    }
  }, [visibleDeals, stages, checkScroll])

  const scroll = (dir: 'left' | 'right') => {
    if (!containerRef.current) return
    const amount = containerRef.current.clientWidth * 0.6
    containerRef.current.scrollBy({
      left: dir === 'left' ? -amount : amount,
      behavior: 'smooth',
    })
  }

  const handleBlocked = useCallback(
    (result: { allowed: boolean; reason?: string }, targetStage: DealStage) => {
      if (result.reason) toast.error(result.reason, { id: `dnd-v2-${targetStage}` })
    },
    [],
  )

  const dnd = useDealsDnd(
    visibleDeals,
    (dealId, newStage) => {
      const current = visibleDeals.find((d) => d.id === dealId)
      if (current?.etapa === newStage) return
      onMoveStage?.(dealId, newStage)
    },
    handleBlocked,
    user?.role,
  )

  const draggedDeal = dnd.draggedDealId
    ? visibleDeals.find((d) => d.id === dnd.draggedDealId)
    : null

  if (loading) {
    return (
      <div className='flex items-center justify-center py-20'>
        <Loader2 size={32} className='animate-spin text-accent' />
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex flex-col items-center gap-3 py-20 text-center'>
        <p className='text-sm text-text-primary'>Erro ao carregar pipeline</p>
        <p className='text-xs text-text-muted'>{error}</p>
        {onRetry && (
          <button type='button' onClick={onRetry} className='text-sm text-accent'>
            <RefreshCw size={14} className='mr-1 inline' /> Tentar novamente
          </button>
        )}
      </div>
    )
  }

  return (
    <div className='deals-v2-board group relative flex flex-col gap-3'>
      <div className='sr-only' aria-live='polite'>
        {dnd.isDragging && draggedDeal ? `Arrastando ${draggedDeal.empresaNome}` : ''}
      </div>

      <div className='z-10 flex w-full items-center justify-between px-1'>
        <button
          type='button'
          onClick={() => scroll('left')}
          disabled={!showLeft}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-lg shadow-md transition-all duration-300',
            showLeft
              ? 'cursor-pointer bg-accent text-white hover:scale-105 hover:bg-accent/90'
              : 'cursor-not-allowed bg-accent/30 text-white/50',
          )}
          aria-label='Rolar para esquerda'
          title='Rolar para esquerda'
        >
          <ChevronLeft size={18} />
        </button>
        <button
          type='button'
          onClick={() => scroll('right')}
          disabled={!showRight}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-lg shadow-md transition-all duration-300',
            showRight
              ? 'cursor-pointer bg-accent text-white hover:scale-105 hover:bg-accent/90'
              : 'cursor-not-allowed bg-accent/30 text-white/50',
          )}
          aria-label='Rolar para direita'
          title='Rolar para direita'
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div
        ref={containerRef}
        onScroll={checkScroll}
        className='deals-v2-columns scrollbar-thin flex gap-4 overflow-x-auto pb-6 pt-1'
      >
        {stages.map((stage) => (
          <KanbanColumnV2
            key={stage}
            stage={stage}
            deals={dealsByStage[stage] ?? []}
            dnd={dnd}
            draggedDeal={draggedDeal}
            onSelectDeal={onSelectDeal}
            onMoveStage={onMoveStage}
            areValuesVisible={areValuesVisible}
          />
        ))}
      </div>
    </div>
  )
}
